// src/pages/auth/Register.jsx
import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useSettings } from "../../context/SettingsContext";
import { useThemeContext } from "../../context/ThemeContext";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Stack,
  FormHelperText,
  Divider,
  useMediaQuery,
  Fade,
  Slide,
  Chip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Storefront,
  Person,
  Email,
  Phone,
  Business,
  AttachMoney,
  AccountBalance,
  CloudUpload,
  CheckCircle,
  Lock,
  ArrowBack,
  ArrowForward,
  Badge,
  Verified,
  Shield,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { authApi } from "../../api/authApi";

const steps = ["Account Details", "Business & KYC"];

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { siteName, siteLogo } = useSettings();
  const { colors } = useThemeContext();

  const isMobile = useMediaQuery("(max-width:600px)");
  const safeSiteName = siteName || "SCITECH"; // ✅ SAFE FALLBACK

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [kycFile, setKycFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const primaryColor = colors?.primaryColor || "#E31E24";
  const primaryDark = colors?.primaryColor
    ? darkenColor(colors.primaryColor, 20)
    : "#B71C1C";
  const isDark = colors?.themeMode === "dark";

  function darkenColor(hex, percent) {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max((num >> 16) - amt, 0);
    const G = Math.max(((num >> 8) & 0x00ff) - amt, 0);
    const B = Math.max((num & 0x0000ff) - amt, 0);
    return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
  }

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "vendor",
    storeName: "",
    storeDescription: "",
    businessEmail: "",
    businessPhone: "",
    businessAddress: "",
    taxId: "",
    bankName: "",
    bankAccountName: "",
    bankAccountNumber: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handleFileChange = (e) => {
    setFileError(null);
    setUploadProgress(0);
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        setFileError("File size must be less than 5MB");
        return;
      }
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        setFileError("Only JPG, PNG, GIF, WEBP, and PDF files are allowed");
        return;
      }
      setKycFile(file);
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        setUploadProgress(progress);
        if (progress >= 100) clearInterval(interval);
      }, 100);
    }
  };

  const validateStep = (step) => {
    if (step === 0) {
      if (
        !formData.name.trim() ||
        !formData.email.trim() ||
        !formData.password ||
        !formData.phone.trim()
      ) {
        setError("Please fill in all required fields.");
        return false;
      }
      if (formData.password.length < 6) {
        setError("Password must be at least 6 characters long.");
        return false;
      }
      if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
        setError("Please enter a valid email address.");
        return false;
      }
      return true;
    }
    if (step === 1) {
      if (
        !formData.storeName.trim() ||
        !formData.businessEmail.trim() ||
        !formData.businessAddress.trim() ||
        !formData.taxId.trim() ||
        !formData.bankName.trim() ||
        !formData.bankAccountName.trim() ||
        !formData.bankAccountNumber.trim()
      ) {
        setError("All business and bank information fields are required.");
        return false;
      }
      if (!kycFile) {
        setError("Please upload a KYC document.");
        return false;
      }
      return true;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setError(null);
      setActiveStep((prev) => prev + 1);
    }
  };
  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(activeStep)) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const apiFormData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined)
          apiFormData.append(key, formData[key]);
      });
      if (kycFile) apiFormData.append("kycDocument", kycFile);
      const response = await authApi.register(apiFormData);
      if (response.data && response.data.token) {
        setSuccess(true);
        const loginResult = await login(formData.email, formData.password);
        if (loginResult.success)
          setTimeout(() => navigate("/vendor/dashboard"), 1000);
        else setTimeout(() => navigate("/login"), 1500);
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError(
        err.response?.data?.message || "Registration failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isDark
          ? "linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #16213e 100%)"
          : `linear-gradient(135deg, ${primaryColor}08 0%, #f0f0f5 50%, #ffffff 100%)`,
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${primaryColor}10 0%, transparent 70%)`,
          animation: "float 25s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "50%": { transform: "translate(-50px, -30px) scale(1.2)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -100,
          left: -100,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${primaryColor}08 0%, transparent 70%)`,
          animation: "float 30s ease-in-out infinite reverse",
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Slide direction="up" in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: { xs: 3, sm: 4 },
              bgcolor: isDark
                ? "rgba(30, 30, 46, 0.92)"
                : "rgba(255, 255, 255, 0.88)",
              backdropFilter: "blur(20px)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.4)"}`,
              boxShadow: isDark
                ? "0 30px 80px rgba(0,0,0,0.5)"
                : "0 30px 80px rgba(0,0,0,0.08)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${primaryColor}, ${primaryDark}, ${primaryColor})`,
                backgroundSize: "200% 100%",
                animation: "gradientMove 3s ease-in-out infinite",
                "@keyframes gradientMove": {
                  "0%": { backgroundPosition: "0% 50%" },
                  "50%": { backgroundPosition: "100% 50%" },
                  "100%": { backgroundPosition: "0% 50%" },
                },
              }}
            />

            <Box sx={{ textAlign: "center", mb: { xs: 2, sm: 3 } }}>
              <Box sx={{ mb: 2 }}>
                {siteLogo ? (
                  <Box
                    sx={{
                      width: isMobile ? 80 : 120,
                      height: isMobile ? 70 : 100,
                      mx: "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {/* ✅ FIX: Use siteLogo DIRECTLY (Context already has full URL) */}
                    <img
                      src={siteLogo}
                      alt={safeSiteName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: isMobile ? 60 : 70,
                      height: isMobile ? 60 : 70,
                      mx: "auto",
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                      borderRadius: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 12px 40px ${primaryColor}4D`,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      sx={{ color: "white", fontWeight: 900 }}
                    >
                      {/* ✅ FIX: Use safeSiteName */}
                      {safeSiteName.charAt(0)}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight="800"
                sx={{
                  color: isDark ? "#ffffff" : "#1A1A1A",
                  fontSize: { xs: "1.3rem", sm: "1.8rem" },
                }}
              >
                Become a Seller
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: isDark ? "#aaaaaa" : "text.secondary",
                  fontSize: { xs: "0.8rem", sm: "0.9rem" },
                }}
              >
                Join {safeSiteName} and start selling your products today!
              </Typography>
            </Box>

            <Stepper
              activeStep={activeStep}
              alternativeLabel={!isMobile}
              sx={{
                mb: { xs: 3, sm: 4 },
                "& .MuiStepLabel-root .Mui-active": { color: primaryColor },
                "& .MuiStepIcon-root.Mui-active": { color: primaryColor },
                "& .MuiStepIcon-root.Mui-completed": { color: primaryColor },
                "& .MuiStepLabel-label": {
                  fontSize: isMobile ? "0.7rem" : "0.875rem",
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Fade in>
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: isDark ? "rgba(244, 67, 54, 0.15)" : undefined,
                    color: isDark ? "#ef5350" : undefined,
                    border: `1px solid ${isDark ? "rgba(244, 67, 54, 0.2)" : "error.main"}`,
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}
            {success && (
              <Fade in>
                <Alert
                  severity="success"
                  sx={{
                    mb: 2,
                    bgcolor: isDark ? "rgba(76, 175, 80, 0.15)" : undefined,
                    color: isDark ? "#66bb6a" : undefined,
                    border: `1px solid ${isDark ? "rgba(76, 175, 80, 0.2)" : "success.main"}`,
                  }}
                >
                  Account created successfully! Redirecting to dashboard...
                </Alert>
              </Fade>
            )}

            <form onSubmit={handleSubmit}>
              {activeStep === 0 && (
                <Stack spacing={2.5}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      {formData.name ? (
                        formData.name[0].toUpperCase()
                      ) : (
                        <Person />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      color={isDark ? "#ffffff" : "text.primary"}
                    >
                      Personal Information
                    </Typography>
                    <Chip
                      label="Step 1 of 2"
                      size="small"
                      sx={{
                        ml: "auto",
                        bgcolor: `${primaryColor}20`,
                        color: primaryColor,
                        fontWeight: 600,
                        fontSize: "0.7rem",
                      }}
                    />
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: isDark
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.04)",
                            },
                            "&:hover fieldset": { borderColor: primaryColor },
                            "&.Mui-focused fieldset": {
                              borderColor: primaryColor,
                              borderWidth: 2,
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: primaryColor,
                          },
                          "& .MuiInputBase-input": {
                            color: isDark ? "#ffffff" : undefined,
                          },
                          "& .MuiInputLabel-root": {
                            color: isDark ? "#aaaaaa" : undefined,
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person
                                sx={{
                                  color: isDark ? "#666" : "text.secondary",
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: isDark
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.04)",
                            },
                            "&:hover fieldset": { borderColor: primaryColor },
                            "&.Mui-focused fieldset": {
                              borderColor: primaryColor,
                              borderWidth: 2,
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: primaryColor,
                          },
                          "& .MuiInputBase-input": {
                            color: isDark ? "#ffffff" : undefined,
                          },
                          "& .MuiInputLabel-root": {
                            color: isDark ? "#aaaaaa" : undefined,
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone
                                sx={{
                                  color: isDark ? "#666" : "text.secondary",
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: isDark
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.04)",
                            },
                            "&:hover fieldset": { borderColor: primaryColor },
                            "&.Mui-focused fieldset": {
                              borderColor: primaryColor,
                              borderWidth: 2,
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: primaryColor,
                          },
                          "& .MuiInputBase-input": {
                            color: isDark ? "#ffffff" : undefined,
                          },
                          "& .MuiInputLabel-root": {
                            color: isDark ? "#aaaaaa" : undefined,
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email
                                sx={{
                                  color: isDark ? "#666" : "text.secondary",
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        required
                        helperText="Must be at least 6 characters"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: isDark
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.04)",
                            },
                            "&:hover fieldset": { borderColor: primaryColor },
                            "&.Mui-focused fieldset": {
                              borderColor: primaryColor,
                              borderWidth: 2,
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: primaryColor,
                          },
                          "& .MuiInputBase-input": {
                            color: isDark ? "#ffffff" : undefined,
                          },
                          "& .MuiInputLabel-root": {
                            color: isDark ? "#aaaaaa" : undefined,
                          },
                          "& .MuiFormHelperText-root": {
                            color: isDark ? "#aaaaaa" : undefined,
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock
                                sx={{
                                  color: isDark ? "#666" : "text.secondary",
                                }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                sx={{
                                  color: isDark ? "#aaa" : "text.secondary",
                                }}
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              )}

              {activeStep === 1 && (
                <Stack spacing={2.5}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Storefront sx={{ color: primaryColor, mr: 1 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color={isDark ? "#ffffff" : "text.primary"}
                      >
                        Store Information
                      </Typography>
                      <Chip
                        label="Step 2 of 2"
                        size="small"
                        sx={{
                          ml: "auto",
                          bgcolor: `${primaryColor}20`,
                          color: primaryColor,
                          fontWeight: 600,
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                    <Divider
                      sx={{
                        mb: 2,
                        borderColor: isDark
                          ? "rgba(255,255,255,0.1)"
                          : undefined,
                      }}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Store Name"
                          name="storeName"
                          value={formData.storeName}
                          onChange={handleChange}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.02)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.04)",
                              },
                              "&:hover fieldset": { borderColor: primaryColor },
                              "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: primaryColor,
                            },
                            "& .MuiInputBase-input": {
                              color: isDark ? "#ffffff" : undefined,
                            },
                            "& .MuiInputLabel-root": {
                              color: isDark ? "#aaaaaa" : undefined,
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Storefront
                                  sx={{
                                    color: isDark ? "#666" : "text.secondary",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Business Email"
                          name="businessEmail"
                          type="email"
                          value={formData.businessEmail}
                          onChange={handleChange}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.02)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.04)",
                              },
                              "&:hover fieldset": { borderColor: primaryColor },
                              "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: primaryColor,
                            },
                            "& .MuiInputBase-input": {
                              color: isDark ? "#ffffff" : undefined,
                            },
                            "& .MuiInputLabel-root": {
                              color: isDark ? "#aaaaaa" : undefined,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Store Description"
                          name="storeDescription"
                          value={formData.storeDescription}
                          onChange={handleChange}
                          multiline
                          rows={3}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.02)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.04)",
                              },
                              "&:hover fieldset": { borderColor: primaryColor },
                              "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: primaryColor,
                            },
                            "& .MuiInputBase-input": {
                              color: isDark ? "#ffffff" : undefined,
                            },
                            "& .MuiInputLabel-root": {
                              color: isDark ? "#aaaaaa" : undefined,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Business Address"
                          name="businessAddress"
                          value={formData.businessAddress}
                          onChange={handleChange}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.02)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.04)",
                              },
                              "&:hover fieldset": { borderColor: primaryColor },
                              "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: primaryColor,
                            },
                            "& .MuiInputBase-input": {
                              color: isDark ? "#ffffff" : undefined,
                            },
                            "& .MuiInputLabel-root": {
                              color: isDark ? "#aaaaaa" : undefined,
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Business
                                  sx={{
                                    color: isDark ? "#666" : "text.secondary",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Business Phone"
                          name="businessPhone"
                          value={formData.businessPhone}
                          onChange={handleChange}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.02)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.04)",
                              },
                              "&:hover fieldset": { borderColor: primaryColor },
                              "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: primaryColor,
                            },
                            "& .MuiInputBase-input": {
                              color: isDark ? "#ffffff" : undefined,
                            },
                            "& .MuiInputLabel-root": {
                              color: isDark ? "#aaaaaa" : undefined,
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone
                                  sx={{
                                    color: isDark ? "#666" : "text.secondary",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        mt: 1,
                      }}
                    >
                      <Verified sx={{ color: primaryColor, mr: 1 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color={isDark ? "#ffffff" : "text.primary"}
                      >
                        Bank & Tax Information (KYC) - Required
                      </Typography>
                    </Box>
                    <Divider
                      sx={{
                        mb: 2,
                        borderColor: isDark
                          ? "rgba(255,255,255,0.1)"
                          : undefined,
                      }}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Tax ID / VAT Number"
                          name="taxId"
                          value={formData.taxId}
                          onChange={handleChange}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.02)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.04)",
                              },
                              "&:hover fieldset": { borderColor: primaryColor },
                              "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: primaryColor,
                            },
                            "& .MuiInputBase-input": {
                              color: isDark ? "#ffffff" : undefined,
                            },
                            "& .MuiInputLabel-root": {
                              color: isDark ? "#aaaaaa" : undefined,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Bank Name"
                          name="bankName"
                          value={formData.bankName}
                          onChange={handleChange}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.02)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.04)",
                              },
                              "&:hover fieldset": { borderColor: primaryColor },
                              "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: primaryColor,
                            },
                            "& .MuiInputBase-input": {
                              color: isDark ? "#ffffff" : undefined,
                            },
                            "& .MuiInputLabel-root": {
                              color: isDark ? "#aaaaaa" : undefined,
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccountBalance
                                  sx={{
                                    color: isDark ? "#666" : "text.secondary",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Bank Account Name"
                          name="bankAccountName"
                          value={formData.bankAccountName}
                          onChange={handleChange}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.02)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.04)",
                              },
                              "&:hover fieldset": { borderColor: primaryColor },
                              "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: primaryColor,
                            },
                            "& .MuiInputBase-input": {
                              color: isDark ? "#ffffff" : undefined,
                            },
                            "& .MuiInputLabel-root": {
                              color: isDark ? "#aaaaaa" : undefined,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Bank Account Number"
                          name="bankAccountNumber"
                          value={formData.bankAccountNumber}
                          onChange={handleChange}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.02)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.04)",
                              },
                              "&:hover fieldset": { borderColor: primaryColor },
                              "&.Mui-focused fieldset": {
                                borderColor: primaryColor,
                                borderWidth: 2,
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: primaryColor,
                            },
                            "& .MuiInputBase-input": {
                              color: isDark ? "#ffffff" : undefined,
                            },
                            "& .MuiInputLabel-root": {
                              color: isDark ? "#aaaaaa" : undefined,
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoney
                                  sx={{
                                    color: isDark ? "#666" : "text.secondary",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Shield sx={{ color: primaryColor, mr: 1 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color={isDark ? "#ffffff" : "text.primary"}
                      >
                        KYC Document Upload (Required)
                      </Typography>
                    </Box>
                    <Divider
                      sx={{
                        mb: 2,
                        borderColor: isDark
                          ? "rgba(255,255,255,0.1)"
                          : undefined,
                      }}
                    />
                    <Box
                      sx={{
                        p: { xs: 2, sm: 3 },
                        border: `2px dashed ${kycFile ? "#4caf50" : primaryColor}`,
                        borderRadius: 2,
                        textAlign: "center",
                        bgcolor: kycFile
                          ? isDark
                            ? "rgba(76, 175, 80, 0.15)"
                            : "#e8f5e9"
                          : "transparent",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <input
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                        style={{ display: "none" }}
                        id="kyc-file-upload"
                        type="file"
                        onChange={handleFileChange}
                      />
                      <label htmlFor="kyc-file-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={
                            kycFile ? <CheckCircle /> : <CloudUpload />
                          }
                          color={kycFile ? "success" : "primary"}
                          sx={{
                            borderColor: kycFile
                              ? "success.main"
                              : primaryColor,
                            color: kycFile ? "success.main" : primaryColor,
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            "&:hover": {
                              borderColor: kycFile
                                ? "success.main"
                                : primaryDark,
                              bgcolor: kycFile
                                ? "rgba(76, 175, 80, 0.08)"
                                : `${primaryColor}15`,
                            },
                          }}
                        >
                          {kycFile
                            ? "Change Document"
                            : "Upload Business License / ID"}
                        </Button>
                      </label>
                      {kycFile && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="body2"
                            color="success.main"
                            fontWeight="600"
                          >
                            ✅ {kycFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({(kycFile.size / 1024).toFixed(1)} KB)
                          </Typography>
                          {uploadProgress < 100 && uploadProgress > 0 && (
                            <Box
                              sx={{
                                mt: 1,
                                width: "100%",
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.1)"
                                  : "#e0e0e0",
                                borderRadius: 1,
                                height: 4,
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${uploadProgress}%`,
                                  bgcolor: primaryColor,
                                  borderRadius: 1,
                                  height: "100%",
                                  transition: "width 0.3s ease",
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      )}
                      {fileError && (
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ mt: 1 }}
                        >
                          ❌ {fileError}
                        </Typography>
                      )}
                      <FormHelperText
                        sx={{ mt: 1, color: isDark ? "#aaaaaa" : undefined }}
                      >
                        Accepted: JPG, PNG, GIF, WEBP, PDF (Max 5MB)
                      </FormHelperText>
                    </Box>
                  </Box>
                </Stack>
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: { xs: 3, sm: 4 },
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: isDark ? "rgba(255,255,255,0.1)" : "grey.200",
                  flexDirection: isMobile ? "column-reverse" : "row",
                  gap: isMobile ? 2 : 0,
                }}
              >
                <Button
                  onClick={
                    activeStep === 0 ? () => navigate("/login") : handleBack
                  }
                  startIcon={activeStep === 0 ? <ArrowBack /> : null}
                  sx={{
                    textTransform: "none",
                    color: isDark ? "#aaaaaa" : undefined,
                    fontWeight: 600,
                    "&:hover": { color: primaryColor },
                    justifyContent: isMobile ? "center" : "flex-start",
                  }}
                  disabled={loading}
                >
                  {activeStep === 0 ? "Already have an account? Login" : "Back"}
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading || success}
                    endIcon={!loading && !success ? <ArrowForward /> : null}
                    sx={{
                      minWidth: isMobile ? "100%" : 150,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      py: isMobile ? 1.5 : 1.8,
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                      boxShadow: `0 8px 24px ${primaryColor}4D`,
                      "&:hover": {
                        boxShadow: `0 12px 40px ${primaryColor}66`,
                        transform: "translateY(-2px)",
                      },
                      "&:active": { transform: "translateY(0)" },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForward />}
                    sx={{
                      minWidth: isMobile ? "100%" : 120,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      py: isMobile ? 1.5 : 1.8,
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                      boxShadow: `0 8px 24px ${primaryColor}4D`,
                      "&:hover": {
                        boxShadow: `0 12px 40px ${primaryColor}66`,
                        transform: "translateY(-2px)",
                      },
                      "&:active": { transform: "translateY(0)" },
                    }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </form>
          </Paper>
        </Slide>
      </Container>
    </Box>
  );
};

export default Register;
